import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { abMakeMediaCard } from '../lib/ab/media-card';
import { ABCitySelect, ABSceneSelect, ABVenueSelect } from '../lib/ab/select-admin';
import { ABNewPicturesArray } from '../lib/ab/widgets-admin';
import { c_cities } from '../lib/mdl/c-city';
import { cCountryDefaultCurrency } from '../lib/mdl/c-country';
import { CEventFlag } from '../lib/mdl/c-event';
import { cEventActiveSessions, CEventAdminView, cEventCreate, cEventDuplicate, cEventGenreTag, cEventNextSession, cEventPageUrl, cEventsProducerView, cEventUpdate, CSession, cSessionCancel, cSessionDefaultInstructions, cSessionsCreate, cSessionUpdate } from '../lib/mdl/c-event-admin';
import { c_event_tags, cEventTagName } from '../lib/mdl/c-event-tag';
import { c_producers, CProducerAutofill, CProducerEmail, cProducerGetAutofill } from '../lib/mdl/c-producer';
import { c_venues } from '../lib/mdl/c-venue';
import { i_events, IEvent, IEventAdminView, iEventGetActiveSessions, ISession } from '../lib/mdl/i-provider';
import { UMediaLink, uMediaLinkParse } from '../lib/mdl/u-media-link';
import { uInputTranslator } from '../lib/mdl/u-translate';
import { the_alt_token } from '../lib/me/login';
import { the_producer } from '../lib/me/producer';
import { uiSetBG } from '../lib/ui/bg';
import { UIDataAdapter, UIInputAdapter } from '../lib/ui/component/adapter';
import { UIButton } from '../lib/ui/component/button';
import { UIChip, UIChips } from '../lib/ui/component/chip';
import { UIDateTimeInput } from '../lib/ui/component/date';
import { UIDialog } from '../lib/ui/component/dialog';
import { UINewI18NInput } from '../lib/ui/component/i18n-input';
import { UIFixedCurrencyInput, UINumberInput, UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIList } from '../lib/ui/component/list';
import { UIMarkdownInput } from '../lib/ui/component/markdown';
import { UISelect } from '../lib/ui/component/select';
import { UITabs } from '../lib/ui/component/tabs';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UIExclusiveToggles, UIMultiToggles, UISwitch, UITickCheckbox, UITickRadio } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { UIMenu, uiShowMenu } from '../lib/ui/menu';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos } from '../lib/ui/types';
import { UIView, UIViewAddSpec, UIViewSpec } from '../lib/ui/view';
import { $analytics } from '../lib/y/analytics';
import { $log } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $form } from '../lib/y/form';
import { $i18n, the_language, yGetI18N, YI18NString, yInterpolate, yIsEmptyI18N } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { $time, yIntervalTime } from '../lib/y/time';
import { YFlags, YId } from '../lib/y/types';
import { ProducerEventBookingsScreen } from './event_bookings';

const tz = 'Europe/Stockholm';

function getTrackProperties(event: CEventAdminView, session?: CSession, session_update?: any) {
    const city = c_cities.maybe(event.city_id)!;
    const venue = c_venues.maybe(event.venue_id)!;

    const properties: { [key: string]: any } = {
        event_name: yGetI18N(event.name),
        event_city: city.name,
        country: city.country,
        venue: venue.name,
        scene: event.scene_id,
        sales_end_hours: session?.sales_end,
        regular_price: session && session.base_price.value / 100,
        no_marketing: event.flags['no_market'],
        hide_event: event.flags['no_feature']
    }

    if(event.event_tag_ids.length) {
        const tag = c_event_tags.maybe(event.event_tag_ids[0]);
        if(tag)
            properties['category'] = cEventTagName(tag);
    }

    if(session) {
        properties.start_time = $time(session.time).js;
        if(session.is_drop_in)
            properties.end_time = $time(session.end_time).js;
        properties.ticket_amount = session.quantity;
    }

    if(session_update) {
        if(session_update.time)
            properties.start_time = $time(session_update.time).js;
        if(session_update.amount) {
            properties.ticket_amount = session_update.amount;
            const change = session_update.amount - (session?.quantity || 0);
            properties.ticket_change = change;
            if(change > 0)
                properties.tickets_added = change;
            else if(change < 0)
                properties.tickets_reduced = -change;
        }
    }

    return properties;
}

class SessionForm extends UIView {
    constructor(event: CEventAdminView, session?: CSession, ok_callback?: () => void, cancel_callback?: () => void) {
        super('div', 'l-form tl-producer-form');

        const is_managed = session != null && session.managed_by_id != null;
        const currency = cCountryDefaultCurrency(c_cities.maybe(event.city_id)?.country || 'SE');

        const instructions_model: { instructions: YI18NString } = {
            instructions: {}
        }

        const instructions_button = new UIButton({
            title: $i18n.pr.session_edit_instructions, left_icon: UIIcon.PEN_TO_SQUARE, classes: 'm-round m-compact ui-tint-subtle', on_click: () => {
                new InstructionsDialog(instructions_model).present();
            }
        })

        const is_drop_in = new UICheckbox({ title: $i18n.aa.f_dropin });
        const time = new UIDateTimeInput({ kind: 'k-subtle', required: true });
        const end_time = new UIDateTimeInput({ kind: 'k-subtle' });
        const duration = new UINumberInput({ kind: 'k-subtle', required: true });
        const sales_end = new UINumberInput({ kind: 'k-subtle', r_deco: $el.I('hours before start'), decimal: true });
        const quantity = new UINumberInput({ kind: 'k-subtle', required: true, min: 2, disabled: is_managed });
        const base_price = new UIFixedCurrencyInput({ kind: 'k-subtle', default_currency: currency });
        const ticket_type_values = [{ key: 'email', title: 'E-ticket' }, { key: 'pickup', title: 'Guestlist' }];
        const ticket_type = new UIExclusiveToggles(ticket_type_values, v => new UITickRadio(v), { classes: 'l-toggle-row' });

        const form = $form({ is_drop_in, time, end_time, duration, quantity, sales_end, base_price, ticket_type });
        const warning = $el.DIV();

        ticket_type.value = 'pickup';

        if(is_managed)
            addTooltip($i18n.pr.events_tooltip_amount_managed, quantity, { offset: 8, position: UIRelPos.Left })

        $on(time, 'value', t => {
            const publish_date = t.add(-49, $time.day);
            warning.textContent = publish_date.is_future ? yInterpolate($i18n.aa.w_publish_date, publish_date.format('d MMMM yyyy')) : '';
        })

        const duration_labeled = new UILabeled(duration, { above: $i18n.aa.f_duration });
        const end_time_labeled = new UILabeled(end_time, { above: $i18n.aa.f_end_time });
        end_time_labeled.ui_hide();
        $on(is_drop_in, 'value', d => {
            if(d) {
                end_time.required = true;
                end_time_labeled.ui_show();
                duration.required = false;
                duration_labeled.ui_hide();
            } else {
                end_time.required = false;
                end_time_labeled.ui_hide();
                duration.required = true;
                duration_labeled.ui_show();
            }
        });

        const tag = cEventGenreTag(event);

        if(tag?.flags.create_dropin) {
            is_drop_in.value = true;
        }

        const buttons = $el.DIV('l-button-row m-right');

        if(cancel_callback) {
            buttons.ui_add(new UIButton({ title: $i18n.tl.b_cancel, classes: 'm-hollow', on_click: cancel_callback }));
        }

        if(session) {
            form.value = session;
            instructions_model.instructions = session.instructions;

            buttons.ui_add(new UIButton({
                title: $i18n.pr.session_update, classes: 'ui-tint-reverse', on_click: () => {
                    const old_amount = session.quantity;
                    const new_amount = quantity.value!;
                    const value = form.value;
                    value.instructions = instructions_model.instructions;

                    const doUpdate = () => {
                        return cSessionUpdate(event, session.id, value).then(() => {
                            const properties = getTrackProperties(event, session, value);
                            $analytics.track('Edited Session', properties);
                            if(new_amount != old_amount)
                                $analytics.track(new_amount > old_amount ? 'Added Tickets' : 'Reduced Tickets', properties)
                            ok_callback?.();
                        }, uiToast);
                    }

                    if(!time.value!.equal($time(session.time)) && session.quantity_reserved) {
                        return showWarning({
                            texts: [$i18n.aa.w_session_booked, yInterpolate($i18n.aa.w_session_booked_previous, $time(session.time).format('yyyy-MM-dd HH:mm')),
                            yInterpolate($i18n.aa.w_session_booked_new, time.value!.format('yyyy-MM-dd HH:mm'))]
                        }).then(doUpdate);
                    } else {
                        return doUpdate();
                    }
                }
            }));
        } else {
            // buttons.ui_add(new UIButton({ title: $i18n.aa.b_add, classes: 'm-round', on_click: () => {
            // }}));

            const tag = cEventGenreTag(event);

            if(tag?.flags.create_dropin) {
                is_drop_in.value = true;
            }

            if(event.sessions.length) {
                const last = event.sessions[event.sessions.length - 1];
                sales_end.value = last.sales_end;
                base_price.value = last.base_price;
                duration.value = last.duration;
                if(!yIsEmptyI18N(last.instructions))
                    instructions_model.instructions = last.instructions;
                else if(last.ticket_type)
                    instructions_model.instructions = cSessionDefaultInstructions(last.ticket_type);
                ticket_type.value = last.ticket_type;
            } else {
                sales_end.value = 2;
                const defaults = c_producers.maybe(event.producer_id)?.event_defaults;
                if(defaults) {
                    if(defaults.base_price)
                        base_price.value = defaults.base_price;
                    if(defaults.sales_end != null)
                        sales_end.value = defaults.sales_end;
                    if(defaults.duration)
                        duration.value = defaults.duration;
                    if(!yIsEmptyI18N(defaults.instructions))
                        instructions_model.instructions = defaults.instructions!;
                    else if(defaults.ticket_type)
                        instructions_model.instructions = cSessionDefaultInstructions(defaults.ticket_type);
                    if(defaults.ticket_type)
                        ticket_type.value = defaults.ticket_type;
                }
            }

            buttons.ui_add(new UIButton({
                title: $i18n.pr.session_create, classes: 'ui-tint-reverse', on_click: () => {
                    if(!form.validate()) return;
                    const value = form.value;
                    value.instructions = instructions_model.instructions;
                    return cSessionsCreate(event, [value]).then(() => {
                        $analytics.track('Added Session', getTrackProperties(event, undefined, value));
                        ok_callback?.();
                    }, uiToast);
                }
            }));
        }

        this.ui_add([
            $el.DIV('l-button-row m-right', [
                instructions_button
            ]),
            $el.DIV('l-input-row m-new m-baseline', [
                new UILabeled(time, { above: $i18n.pr.session_date }),
                // new UILabeled(time, { above: $i18n.pr.session_time }),
                end_time_labeled,
                duration_labeled,
                new UILabeled(sales_end, { above: $i18n.pr.session_booking_stop, below: $i18n.aa.f_i_sales_end_sub })
            ]),
            $el.DIV('l-input-row m-new m-baseline', [
                new UILabeled(quantity, { above: $i18n.aa.f_ticket_amount }),
                new UILabeled(base_price, { above: $i18n.aa.f_i_regular_price }),
            ]),
            ticket_type,
            buttons,
            warning
        ]);
    }
}

class SessionDialog extends UIDialog {
    constructor(event: CEventAdminView, session?: CSession, callback?: () => void) {
        super({ classes: 'm-1000 m-mobile-fs' });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            new SessionForm(event, session, () => { this.dismiss(null); callback?.() }, () => this.dismiss(null))
        ]));
    }
}

class InfoTab extends UIView {
    constructor(buttons: UIViewSpec, event?: CEventAdminView, duplicate?: CEventAdminView, add_callback?: (event: CEventAdminView) => void, autofill?: CProducerAutofill, imported?: IEvent) {
        super('div', 'tl-admin-page');

        buttons.ui_empty(new UIButton({
            classes: 'm-compact', title: $i18n.pr.event_save, on_click: () => {
                if(!form.validate()) return;
                const value = form.value;
                value.event_tag_ids = genre.value;
                const flags: YFlags<CEventFlag> = {
                    no_feature: dont_feature.value || undefined,
                    no_market: dont_market.value || undefined,
                }
                value.flags = flags;

                if(!event) {
                    value.producer_id = the_producer!.id;
                    if(imported)
                        value.imported_id = imported.id;

                    $log('app', 'value', value);
                    return cEventCreate(value).then(event => {
                        $analytics.track('Event info saved', getTrackProperties(event));
                        add_callback?.(event);
                    }, uiToast);
                } else {
                    $log('app', 'value', value);
                    return cEventUpdate(event, value).then(e => {
                        $analytics.track('Edited Event Info', getTrackProperties(e));
                        $location.pop();
                    }, uiToast);
                }
            }
        }));

        const model = event ?? duplicate;

        const genre_values = c_event_tags.all.filter(t => t.is_genre).map(t => ({ key: t.id, name: cEventTagName(t) }));
        const genre_select = new UISelect(genre_values, { kind: 'k-subtle', placeholder: $i18n.pr.event_genre, searchable: genre_values.length > 10, compact_title: $i18n.pr.event_genre_add });
        const genre = new UIChips<YId>({ to_chip: c => cEventTagName(c_event_tags.maybe(c)!), classes: 'm-collapse-empty' });

        const name = new UINewI18NInput(new UITextInput({ kind: 'k-subtle' }), { required: true, button_classes: 'ui-tint-fg', translator: uInputTranslator });
        const city_id = new ABCitySelect({ kind: 'k-subtle', required: true, searchable: true });
        const venue_id = new ABVenueSelect({ kind: 'k-subtle', required: true });
        $on(city_id, 'value', city => { if(city) venue_id.city = city });
        const scene_id = new ABSceneSelect({ kind: 'k-subtle', required: true });
        $on(venue_id, 'value', venue => { scene_id.venue = venue });

        const description = new UINewI18NInput(new UIMarkdownInput({ lines: 8, resizeable: true, kind: 'k-subtle' }), { button_classes: 'ui-tint-fg', translator: uInputTranslator })

        const addMediaLink = () => {
            uMediaLinkParse(media_link.value).then(link => {
                media_links.push(link);
                media_link.value = '';
            }, uiToast);
        }

        const media_link = new UITextInput({ kind: 'k-subtle', placeholder: $i18n.pr.event_add_media_link });
        const media_link_add = new UIButton({ title: $i18n.pr.event_add_link, classes: 'm-round m-compact ui-tint-subtle', on_click: addMediaLink });
        media_link.on_enter = addMediaLink;

        const media_links = new UIInputAdapter((l: UMediaLink, entry) => {
            return {
                view: abMakeMediaCard(l, () => { media_links.remove(entry) }),
                input: $form.noop()
            };
        });

        const photo_credits = new UITickCheckbox({ title: $i18n.pr.event_photo_credits, classes: 't-sz-small' });
        const picture_ids = new ABNewPicturesArray($i18n.aa.b_add_pictures, 'event', !model, false, 'm-first-big', photo_credits.value);

        $on(photo_credits, 'value', v => {
            picture_ids.credits = v;
        })

        const team_emails_values = autofill?.booking_users?.map(t => ({ key: t.email, name: t.name + ' (' + t.email + ')' }));
        const team_emails = new UISelect(team_emails_values, { kind: 'k-subtle', placeholder: $i18n.pr.event_email_team, searchable: team_emails_values ? team_emails_values.length > 10 : false });

        const addOtherEmail = () => {
            const key = other_emails.value;
            if(!key) return;
            other_emails.value = '';
            if(!producer_emails.value.find(s => s.email == key))
                producer_emails.push({ email: key, kinds: ['booking_list'] });
        }

        const other_emails = new UITextInput({ kind: 'k-subtle', placeholder: $i18n.pr.event_email_external });
        const other_emails_add = new UIButton({ title: $i18n.aa.b_add, classes: 'm-round m-compact ui-tint-subtle', on_click: addOtherEmail });
        other_emails.on_enter = addOtherEmail;

        const producer_emails = new UIInputAdapter((e: CProducerEmail, entry) => {
            const container = $el.DIV('tl-producer-message i-input');
            const email = new UIChip(e.email, 'm-full');
            const kind_values = [{ key: 'booking_list', title: 'Booking list' }, { key: 'fully_booked', title: 'Fully booked' }];
            const kinds = new UIMultiToggles(kind_values, v => new UITickCheckbox(v), { classes: 'l-toggle-row' });
            $on(email, 'remove', () => { producer_emails.remove(entry) });
            container.ui_add([email, kinds]);

            return {
                view: container,
                input: $form({ email: $form.noop(), kinds })
            };
        });

        const dont_market = new UISwitch;
        const dont_feature = new UISwitch;

        const form = $form({ _: [genre, dont_market, dont_feature], city_id, venue_id, scene_id, name, description, picture_ids: picture_ids.adapter, media_links, producer_emails });

        $on(genre_select, 'value', key => {
            if(!key) return;
            genre_select.value = undefined;
            const tag = c_event_tags.maybe(key);
            if(!tag) return;
            const value = genre.value;
            if(!value.includes(key))
                genre.value = [...value, key];
        });

        $on(genre, 'value', value => {
            genre_select.compact_mode = value.length > 0;
        });

        $on(team_emails, 'value', key => {
            if(!key) return;
            team_emails.value = undefined;
            if(!producer_emails.value.find(s => s.email == key))
                producer_emails.push({ email: key, kinds: ['booking_list'] });
        });

        if(model) {
            venue_id.city = model.city_id;
            scene_id.venue = model.venue_id;
            form.value = model;
            genre.value = model.event_tag_ids.filter(t => c_event_tags.maybe(t)?.is_genre);
            dont_market.value = model.flags.no_market;
            dont_feature.value = model.flags.no_feature;
        } else {
            picture_ids.adapter.value = [undefined];

            if(imported) {
                name.value = yIsEmptyI18N(imported.name) ? { [the_language]: '' } : imported.name;
                description.value = yIsEmptyI18N(imported.description) ? { [the_language]: '' } : imported.description;
                // length.value = imported.length;
                // regular_price.value = imported.regular_price;

                const defaults = c_producers.maybe(imported.producer_id)?.event_defaults;
                if(defaults) {
                    if(defaults.city_id) {
                        city_id.value = defaults.city_id;
                        if(defaults.venue_id) {
                            venue_id.value = defaults.venue_id;
                            if(defaults.scene_id)
                                scene_id.value = defaults.scene_id;
                        }
                    }
                    if(defaults.event_tag_ids?.length)
                        genre.value = defaults.event_tag_ids;
                }
            } else {
                name.value = { [the_language]: '' };
                description.value = { [the_language]: '' };
                const p = the_producer!.id;
                if(p) {
                    const defaults = c_producers.maybe(p)?.event_defaults;
                    if(defaults) {
                        if(defaults.city_id) {
                            city_id.value = defaults.city_id;
                            if(defaults.venue_id) {
                                venue_id.value = defaults.venue_id;
                                if(defaults.scene_id)
                                    scene_id.value = defaults.scene_id;
                            }
                        }
                        if(defaults.event_tag_ids?.length)
                            genre.value = defaults.event_tag_ids;
                        if(!yIsEmptyI18N(defaults.description))
                            description.value = defaults.description;
                        if(defaults.picture_ids?.length)
                            picture_ids.adapter.value = defaults.picture_ids;
                        if(defaults.event_flags?.no_market)
                            dont_market.value = true;
                    }
                };
            }
        }

        const media_icons = $el.DIV('tl-producer-media-icons', [
            $el.ICON(UIIcon.BRAND_YOUTUBE),
            $el.ICON(UIIcon.BRAND_SPOTIFY),
            $el.ICON(UIIcon.BRAND_SOUNDCLOUD),
            $el.ICON(UIIcon.BRAND_VIMEO)
        ]);

        this.ui_add($el.DIV('ui-section tl-producer-section', $el.FORM('ui-section w-producer-form m-centered l-form tl-producer-form', [
            new UILabeled($el.DIV('l-form', [
                genre,
                genre_select
            ]), { above: $i18n.aa.f_genre }),
            new UILabeled(name, { above: $i18n.aa.f_event_name, required: true }),
            new UILabeled(city_id, { above: $i18n.aa.f_city, required: true }),
            new UILabeled(venue_id, { above: $i18n.aa.f_choose_venue, required: true }),
            new UILabeled(scene_id, { above: 'Scene', required: true }),
            new UILabeled(description, { above: $i18n.aa.f_description }),
            new UILabeled($el.DIV('l-form', [
                $el.DIV('l-input-row', [media_link, media_link_add]),
                new UIList(media_links, { classes: 'tl-media-chips i-input' }),
            ]), { above: $i18n.aa.f_media_links, above_right: media_icons }),
            // new UILabeled(media_youtube, { above: $i18n.aa.f_media_links }),
            // media_vimeo,
            // media_spotify,
            // media_soundcloud,
            new UILabeled(picture_ids, { above: $i18n.aa.f_pictures, above_right: photo_credits }),
            picture_ids.add_button,
            new UILabeled($el.DIV('l-form', [
                team_emails,
                $el.DIV('l-input-row', [other_emails, other_emails_add]),
                new UIList(producer_emails, { classes: 'l-form' }),
            ]), { above: $i18n.aa.f_list_recipients }),
            $el.DIV('tl-producer-switch-block i-input', [
                $el.DIV('r-texts', [
                    $el.B($i18n.aa.f_dont_market),
                    $el.DIV('r-description', $i18n.aa.f_dont_market_sub)
                ]),
                $el.S(),
                dont_market
            ]),
            $el.DIV('tl-producer-switch-block i-input', [
                $el.DIV('r-texts', [
                    $el.B($i18n.aa.f_dont_feature),
                    $el.DIV('r-description', $i18n.aa.f_dont_feature_sub)
                ]),
                $el.S(),
                dont_feature
            ]),
            $el.DIV('s-section')
        ])));
    }
}

class SessionCard extends UIView {
    constructor(session: CSession, event: CEventAdminView, update_callback?: () => void) {
        const booked_span = $el.I(session.quantity_reserved + ' / ' + session.quantity)

        if(session.quantity_booked != session.quantity_reserved) {
            addTooltip(yInterpolate($i18n.aa.tt_session_booked, session.quantity_booked, session.quantity_reserved - session.quantity_booked), booked_span,
                { position: UIRelPos.Above, offset: 5 });
            booked_span.classList.add('tl-tooltip-underline');
        }

        const booked_text: UIViewAddSpec = [booked_span, ' ' + $i18n.aa.c_booked];

        if(session.quantity_standby && $time(session.time).is_future)
            booked_text.push(' | ' + session.quantity_standby + ' ' + $i18n.aa.c_standby);

        const edit = new UIButton({ icon: UIIcon.PEN_TO_SQUARE, classes: 'ui-tint-fg' });
        const edit_session = () => {
            new SessionDialog(event, session, update_callback).present();
        };
        edit.on_click = edit_session;

        const cancel = new UIButton({ icon: UIIcon.TRASH_CAN, classes: 'ui-tint-fg' });
        const cancel_session = () => {
            if(session.quantity_reserved) {
                return showWarning({ text: $i18n.aa.w_cancel_booked }).then(() => {
                    return cSessionCancel(session.id).then(() => {
                        $analytics.track('Canceled Session', getTrackProperties(event, session));
                        update_callback?.();
                    });
                });
            } else {
                return cSessionCancel(session.id).then(() => {
                    $analytics.track('Canceled Session', getTrackProperties(event, session));
                    update_callback?.();
                });
            }
        };
        cancel.on_click = cancel_session;

        const ellipsis = new UIButton({
            icon: UIIcon.ELLIPSIS_STROKE, classes: 'ui-tint-fg t-sz-large q-m', on_click: () => {
                const menu = new UIMenu([
                    { icon: UIIcon.PEN_TO_SQUARE, title: "Edit session", action: edit_session },
                    (session.ticket_type == 'email') ?
                        { icon: UIIcon.LIST_UL, title: $i18n.pr.event_booking_list, action: () => { $location.push({ transient: new ProducerEventBookingsScreen(event, session) }) } } :
                        { icon: UIIcon.LIST_UL, title: $i18n.pr.event_booking_list, link: `/booking_list_redirect/${session.id}?token=${the_alt_token}` },
                    { icon: UIIcon.TRASH_CAN, title: "Cancel session", action: cancel_session },
                ]);

                uiShowMenu(menu, ellipsis);
            }
        });

        super('div', 'tl-producer-card m-session', [
            $el.DIV('r-date', [
                $el.DIV('r-day', $time(session.time).format('dd')),
                $el.DIV('r-month', $time(session.time).format('MMM'))
            ]),
            $el.DIV('r-info', [
                $el.B($time(session.time).format('yyyy-MM-dd HH:mm')),
                $el.DIV('tl-card-info', booked_text)
            ]),
            $el.S(),
            // new UIButton({kind: 'k-square', icon: UIIcon.DOWNLOAD, classes: 'm-round m-bigger ui-tint-subtle', link: '/docs/producer_invoice/'+payment.id+'?format=pdf&token='+$tl.the_login!.alt_token, on_click: () => { $analytics.track('Summary Download') } })
            $el.DIV('r-buttons q-td', [
                edit,
                cancel,
                (session.ticket_type == 'email') ?
                    new UIButton({
                        title: $i18n.pr.event_booking_list, icon: UIIcon.LIST_UL, kind: 'k-default', classes: 'm-round ui-tint-subtle m-compact', on_click: () => {
                            $location.push({ transient: new ProducerEventBookingsScreen(event, session) })
                        }
                    }) :
                    new UIButton({ title: $i18n.pr.event_booking_list, icon: UIIcon.LIST_UL, kind: 'k-default', classes: 'm-round ui-tint-subtle m-compact', link: '/booking_list_redirect/' + session.id + '?token=' + the_alt_token }),
            ]),

            ellipsis,
        ]);
    }
}

class ImportedSessionCard extends UIView {
    session: ISession;
    private _checkbox: UICheckbox;

    constructor(session: ISession) {
        super('div', 'tl-admin-card');

        this.session = session;

        this.ui_add($el.DIV('r-main', [
            this._checkbox = new UICheckbox,
            $el.DIV('l-v', [
                $el.B(yIntervalTime($time(session.time), undefined, tz)),
                // $el.B(yInterpolate($i18n.aa.c_tickets_available, session.available), 'tl-card-info')
            ])
        ]));
    }

    get selected() {
        return this._checkbox.value;
    }
}

// class ImportSessionsDialog extends UIDialog
// {
//     constructor(event: LegacyEvent, sessions: TLImportedSession[])
//     {
//         super({ close: true, classes: 'm-500' });

//         const amount = new UINumberInput({ required: true });

//         this.content.ui_add($el.DIV('w-block l-form m-center',[
//             $el.H2('title', event.name),
//             sessions.map(s => $el.H3('heading', s.time_y.format('yyyy-MM-dd HH:mm'))),
//             new UILabeled(amount, { above: $i18n.aa.f_tickets_per_session }),
//             $el.DIV('l-button-row', [
//                 new UIButton({ title: $i18n.tl.b_cancel, classes: 'm-round m-compact', on_click: () => { this.dismiss(null) } }),
//                 new UIButton({ title: $i18n.aa.b_import, classes: 'm-round m-compact m-hollow', on_click: () => {
//                     const value = amount.value || 0;
//                     const new_sessions = sessions.map(s => ({ time: s.time_y, amount: value, imported: s.id }));
//                     return cSessionsCreate($id.objectIdToId(event.model.id), new_sessions).then(() => {
//                         this.dismiss(true);
//                     }, uiToast

class InstructionsDialog extends UIDialog {
    constructor(model: { instructions: YI18NString }) {
        super({ close: true, classes: 'm-700 m-small-padding' });

        const instructions = new UINewI18NInput(new UITextInput({ lines: 8, kind: 'k-subtle' }), { required: true, button_classes: 'ui-tint-fg', translator: uInputTranslator });
        instructions.value = model.instructions;

        this.content.ui_add($el.DIV('w-block l-form', [
            instructions,
            $el.DIV('l-button-row m-right', [
                new UIButton({
                    title: $i18n.pr.event_save, classes: 'm-round m-compact ui-tint-fg', on_click: () => {
                        model.instructions = instructions.value || {};
                        this.dismiss(null);
                    }
                }),
            ])
        ]));
    }
}

class SessionsTab extends UIView {
    constructor(buttons: UIViewSpec, event: CEventAdminView, create?: boolean) {
        super('div', 'tl-admin-page');

        const update_buttons = () => {
            buttons.ui_empty();

            const active_sessions = cEventActiveSessions(event);

            if(active_sessions.length) {
                if(create) {
                    buttons.ui_add(new UIButton({
                        classes: 'm-compact', title: $i18n.pr.event_send_for_review, on_click: () => {
                            $analytics.track('Event sent for review', getTrackProperties(event));
                            $location.pop();
                        }
                    }));
                }

                buttons.ui_add(new UIButton({
                    classes: 'm-compact', left_icon: UIIcon.PLUS, title: $i18n.pr.event_create_new_session, on_click: () => {
                        new SessionDialog(event, undefined, refresh_sessions).present();
                    }
                }));

                session_section.ui_hide();
            } else {
                session_section.ui_show();
            }
        }

        const refresh_sessions = () => {
            sessions.data = cEventActiveSessions(event);
            update_buttons();
        };

        const session_section = $el.DIV('ui-section tl-producer-section m-create', new SessionForm(event, undefined, refresh_sessions));

        this.ui_add(session_section);

        const sessions = new UIDataAdapter((s: CSession) => new SessionCard(s, event, refresh_sessions), cEventActiveSessions(event));
        update_buttons();

        this.ui_add($el.DIV('ui-section', $el.DIV('tl-new-list-block m-no-padding', [
            new UIList(sessions, { classes: 'r-grid' })
        ])));

        if(event.imported_id) {
            const imported_sessions = new UIDataAdapter((s: ISession) => new ImportedSessionCard(s));
            let all_sessions: ISession[];
            i_events.get(event.imported_id).then(e => {
                all_sessions = e.sessions;
                const now = $time.now();
                imported_sessions.data = all_sessions.filter(i_s => $time(i_s.time).after(now) && !cEventActiveSessions(event).some(e_s => $time(i_s.time).equal($time(e_s.time))));
            });

            // container.ui_add([
            //     $el.B($i18n.aa.h_available_sessions),
            //     new UIList(imported_sessions, { classes: 'r-check-grid' }),
            //     new UIButton({ title: $i18n.aa.b_import_selected, classes: 'm-round m-compact', on_click: () => {
            //         const sessions: TLImportedSession[] = [];
            //         for(const v of imported_sessions.views) {
            //             if(v.selected)
            //                 sessions.push(v.session);
            //         }

            //         if(sessions.length) {
            //             new ImportSessionsDialog(event, sessions).present().then(() => {
            //                 refresh_sessions();
            //                 if(all_sessions) {
            //                     const now = $time.now();
            //                     imported_sessions.data = all_sessions.filter(i_s => i_s.time_y.after(now) && !event.active_sessions.some(e_s => i_s.time_y.equal(e_s.time_y)));
            //                 }
            //             });
            //         }
            //     }})
            // ]);
        }

        // this.ui_add(container);
    }
}

interface EventScreenOptions {
    event?: CEventAdminView;
    duplicate?: CEventAdminView;
    manage?: boolean;
    autofill?: CProducerAutofill;
    imported_event?: IEvent;
}

export class ProducerEventScreen extends UIScreen {
    constructor(private _options: EventScreenOptions) {
        super({ no_crumbs: true });
        this.title = yGetI18N(_options.event?.name) || $i18n.aa.n_new_event;
    }

    protected override async fillContent(content: UIViewSpec) {
        const options = this._options;

        let event = options.event;
        const original_create = !event;
        let duplicate: CEventAdminView | undefined;
        if(options.duplicate && original_create) {
            duplicate = cEventDuplicate(options.duplicate);
        }

        if(!options.autofill) {
            options.autofill = await cProducerGetAutofill(the_producer!.id);
        }

        let add_callback: ((e: CEventAdminView) => void) | undefined = (e: CEventAdminView) => {
            event = e;
            duplicate = undefined;
            add_callback = undefined;
            tabs.reloadWhen();
            tabs.setCurrent(1);
        }

        const buttons = $el.DIV();

        const tabs = new UITabs([
            { title: $i18n.aa.tb_event_description, view: () => new InfoTab(buttons, event, duplicate, add_callback, options.autofill, options.imported_event) },
            { title: $i18n.aa.tb_date_time, view: () => new SessionsTab(buttons, event!, original_create), when: () => !!event }
        ], {
            classes: 'tl-new-producer-tabs m-fixed w-block-1000 m-top', tabs_placeholder: true, extra_buttons: buttons, back_button_action: () => {
                $location.pop();
            }
        });

        content.ui_add(tabs);

        if(options.manage && event)
            tabs.setCurrent(1);
    }
}

class EventCard extends UIView {
    constructor(event: CEventAdminView, autofill?: CProducerAutofill) {
        super('div', 'tl-new-producer-card');

        const venue = c_venues.maybe(event.venue_id);

        if(!venue) {
            $log('app', 'EventCard missing venue for event', event);
        }

        // const sessions = event.active_sessions;
        // const booked = sessions.reduce((v,s) => v+s.amount_booked, 0);
        // const reserved = sessions.reduce((v,s) => v+s.amount_reserved, 0);
        // const standby = sessions.filter(s => s.time_y.is_future).reduce((v,s) => v+s.amount_standby, 0);
        // const total = sessions.reduce((v,s) => v+s.amount, 0);

        // const edit = new UIButton({ title: $i18n.aa.b_edit, icon: UIIcon.PEN_TO_SQUARE });
        // edit.on_click = () => {
        // }
        // const manage = new UIButton({ title: $i18n.aa.b_add_tickets, icon: UIIcon.PLUS });
        // manage.on_click = () => {
        // }

        const image = $el.DIV('r-image');
        uiSetBG(image, { media: event.picture_ids[0], variant: 'size-500' });

        const session_chips = $el.DIV('r-chips m-gap');
        const sessions = cEventActiveSessions(event);

        for(let i = 0; i < sessions.length; i++) {
            if(i == 3) {
                session_chips.ui_add($el.DIV('tl-producer-chip', yInterpolate($i18n.pr.events_more, sessions.length - 3)));
                break;
            }
            const session = sessions[i];
            const session_time = $time(session.time);
            session_chips.ui_add($el.DIV('tl-producer-chip', [$el.SPAN('t-w-bold t-uppercase', session_time.format('d MMM')), ' ', $el.I(session_time.format('HH:mm'))]));
        }

        const status_chip = $el.DIV('tl-producer-chip');
        let main_button: UIButton | undefined;

        if(event.is_published) {
            const next_session = cEventNextSession(event);
            if(next_session) {
                status_chip.classList.add('m-green');
                status_chip.ui_add($i18n.pr.events_status_live);
                main_button = new UIButton({
                    title: $i18n.pr.events_add_tickets, left_icon: UIIcon.PLUS, classes: 'm-round m-compacter ui-tint-subtle', on_click: () => {
                        $location.push({ transient: new ProducerEventScreen({ event: event, manage: true, autofill }) });
                    }
                });
            } else {
                status_chip.classList.add('m-gray');
                status_chip.ui_add($i18n.pr.events_status_past);
                main_button = new UIButton({
                    title: $i18n.pr.events_add_sessions, left_icon: UIIcon.PLUS, classes: 'm-round m-compacter ui-tint-subtle', on_click: () => {
                        $location.push({ transient: new ProducerEventScreen({ event: event, manage: true, autofill }) });
                    }
                });
            }
        } else {
            if(event.sessions.length) {
                status_chip.classList.add('m-yellow');
                status_chip.ui_add($i18n.pr.events_status_sent);
            } else {
                status_chip.classList.add('m-hollow');
                status_chip.ui_add($i18n.pr.events_status_draft);
            }

            main_button = new UIButton({
                title: $i18n.pr.events_add_sessions, left_icon: UIIcon.PLUS, classes: 'm-round m-compacter ui-tint-subtle', on_click: () => {
                    $location.push({ transient: new ProducerEventScreen({ event: event, manage: true, autofill }) });
                }
            });
        }

        // const booked_span = $el.I(reserved + ' / ' + total)

        // if(booked != reserved) {
        //     addTooltip(yInterpolate($i18n.aa.tt_session_booked, booked, reserved - booked), booked_span, { position: UIRelPos.Above, offset: 5 });
        //     booked_span.classList.add('tl-tooltip-underline');
        // }

        // const booking_text: UIViewAddSpec = [booked_span, ' ' + $i18n.aa.c_booked];

        // if(standby) {
        //     booking_text.push(' | ' + standby + ' ' + $i18n.aa.c_standby);
        //     const info = $el.ICON(UIIcon.CIRCLE_INFO | UIIcon.WEIGHT_SOLID);
        //     addTooltip([$i18n.aa.tt_add_amount, 
        //                     $el.A('https://support.partners.abundolive.se/hantera-evenemang/hur-fungerar-standby', $i18n.tl.b_read_more, 'u-link')], this, 
        //                     { offset: 8, click: info, anchor: info, position: UIRelPos.Above, classes: 'm-clickable' });
        //     booking_text.push(info);
        // }

        // const chips = $el.DIV('r-chips');

        const ellipsis = new UIButton({
            icon: UIIcon.ELLIPSIS_STROKE, classes: 'ui-tint-fg t-sz-large', on_click: () => {
                const menu = new UIMenu([
                    { icon: UIIcon.ARROW_UP_RIGHT_FROM_SQUARE, title: $i18n.pr.event_show, link: cEventPageUrl(event) },
                    {
                        icon: UIIcon.LINK, title: $i18n.pr.event_copy_url, action: () => {
                            navigator.clipboard.writeText(cEventPageUrl(event)).then(() => {
                                uiToast($i18n.pr.event_link_copied);
                            });
                        }
                    },
                    {
                        icon: UIIcon.PEN_TO_SQUARE, title: $i18n.pr.event_edit, action: () => {
                            $location.push({ transient: new ProducerEventScreen({ event: event, autofill }) });
                        }
                    },
                    {
                        icon: UIIcon.PLUS, title: $i18n.pr.event_add_session, action: () => {
                            $location.push({ transient: new ProducerEventScreen({ event: event, manage: true, autofill }) });
                        }
                    },
                    {
                        icon: UIIcon.COPY, title: $i18n.pr.event_duplicate, action: () => {
                            $analytics.track('Event creation started', { duplicated: true })
                            $location.push({ transient: new ProducerEventScreen({ duplicate: event, autofill }) });
                        }
                    },
                    {
                        icon: UIIcon.TRASH_CAN, title: $i18n.pr.event_cancel, action: () => {
                            $location.push({ transient: new ProducerEventScreen({ event: event, autofill, manage: true }) });
                        }
                    },
                ])

                uiShowMenu(menu, ellipsis);
            }
        });

        const event_name = $el.SPAN('u-clickable', yGetI18N(event.name));
        event_name.addEventListener('click', () => {
            $location.push({ transient: new ProducerEventScreen({ event: event, autofill }) });
        });

        this.ui_add([
            $el.DIV('r-main', [
                image,
                $el.DIV('r-texts m-main', [
                    $el.DIV('r-title', [event_name, '\u00a0\u00a0\u00a0', $el.A(cEventPageUrl(event), $el.ICON(UIIcon.ARROW_UP_RIGHT_FROM_SQUARE, 't-sz-small'))]),
                    $el.I(venue?.name ?? ''),
                    $el.S(),
                    session_chips
                ]),
                $el.DIV('r-texts', [
                    $el.S(),
                    $el.DIV('r-chips', status_chip)
                ]),
                $el.S(),
                main_button,
                ellipsis
            ])
        ]);
    }
}

class ImportedCard extends UIView {
    constructor(event: IEventAdminView, autofill?: CProducerAutofill) {
        super('div', 'tl-new-producer-card');

        const venue = c_venues.maybe(event.venue_id!)!;

        // const sessions = event.active_sessions;
        // const booked = sessions.reduce((v,s) => v+s.amount_booked, 0);
        // const reserved = sessions.reduce((v,s) => v+s.amount_reserved, 0);
        // const standby = sessions.filter(s => s.time_y.is_future).reduce((v,s) => v+s.amount_standby, 0);
        // const total = sessions.reduce((v,s) => v+s.amount, 0);

        // const edit = new UIButton({ title: $i18n.aa.b_edit, icon: UIIcon.PEN_TO_SQUARE });
        // edit.on_click = () => {
        // }
        // const manage = new UIButton({ title: $i18n.aa.b_add_tickets, icon: UIIcon.PLUS });
        // manage.on_click = () => {
        // }

        const image = $el.DIV('r-image');
        if(event.picture_urls)
            uiSetBG(image, { url: event.picture_urls[0] });

        const session_chips = $el.DIV('r-chips m-gap');
        const sessions = iEventGetActiveSessions(event);

        for(let i = 0; i < sessions.length; i++) {
            if(i == 3) {
                session_chips.ui_add($el.DIV('tl-producer-chip m-hollow', yInterpolate($i18n.pr.events_more, sessions.length - 3)));
                break;
            }
            const session = sessions[i];
            session_chips.ui_add($el.DIV('tl-producer-chip m-hollow', [$el.SPAN('t-w-bold t-uppercase', $time(session.time).format('d MMM')), ' ', $el.I($time(session.time).format('HH:mm'))]));
        }

        const status_chip = $el.DIV('tl-producer-chip');
        let main_button: UIButton | undefined;

        status_chip.classList.add('m-hollow');
        status_chip.ui_add($i18n.pr.events_status_imported);
        main_button = new UIButton({
            title: $i18n.pr.event_create, classes: 'm-round m-compacter ui-tint-subtle', on_click: () => {
                $location.push({ transient: new ProducerEventScreen({ imported_event: event, autofill }) });
            }
        });

        // const booked_span = $el.I(reserved + ' / ' + total)

        // if(booked != reserved) {
        //     addTooltip(yInterpolate($i18n.aa.tt_session_booked, booked, reserved - booked), booked_span, { position: UIRelPos.Above, offset: 5 });
        //     booked_span.classList.add('tl-tooltip-underline');
        // }

        // const booking_text: UIViewAddSpec = [booked_span, ' ' + $i18n.aa.c_booked];

        // if(standby) {
        //     booking_text.push(' | ' + standby + ' ' + $i18n.aa.c_standby);
        //     const info = $el.ICON(UIIcon.CIRCLE_INFO | UIIcon.WEIGHT_SOLID);
        //     addTooltip([$i18n.aa.tt_add_amount, 
        //                     $el.A('https://support.partners.abundolive.se/hantera-evenemang/hur-fungerar-standby', $i18n.tl.b_read_more, 'u-link')], this, 
        //                     { offset: 8, click: info, anchor: info, position: UIRelPos.Above, classes: 'm-clickable' });
        //     booking_text.push(info);
        // }

        // const chips = $el.DIV('r-chips');


        this.ui_add([
            $el.DIV('r-main', [
                image,
                $el.DIV('r-texts m-main', [
                    $el.DIV('r-title', yGetI18N(event.name)),
                    $el.I(venue.name),
                    $el.S(),
                    session_chips
                ]),
                $el.DIV('r-texts', [
                    $el.S(),
                    $el.DIV('r-chips', status_chip)
                ]),
                $el.S(),
                main_button
            ])
        ]);
    }
}

function newCalcEventSort(e: CEventAdminView): [number, number] {
    const next_session = cEventNextSession(e);
    if(e.is_published) {
        if(next_session) {
            return [0, -$time(next_session.time).n];
        } else if(e.sessions.length) {
            return [3, -$time(e.sessions[e.sessions.length - 1].time).n];
        } else {
            return [3, 0];
        }
    } else {
        if(next_session) {
            return [1, -$time(next_session.time).n];
        } else if(e.sessions.length) {
            return [1, -$time(e.sessions[e.sessions.length - 1].time).n];
        } else {
            return [1, 0];
        }
    }
}

// function calcImportedSort(i: IEventAdminView): [number, number] {
//     const next = iEventGetNextSession(i);
//     return [2, next ? -$time(next.time).n : 0];
// }

type EventEntry = { sort: [number, number], event: CEventAdminView, imported?: undefined } | { sort: [number, number], event?: undefined, imported: IEventAdminView };

export class ProducerEventsScreen extends UIScreen {
    private _grid!: UIDataAdapter<EventEntry, UIView>;
    private _all_events!: EventEntry[];
    private _filter_bar!: ABFilterBar;

    constructor() {
        super();
    }

    protected override async fillContent(content: UIViewSpec) {
        const doSearch = () => this._doSearch();

        // let [all_events, all_imported] = await Promise.all([
        //     cEventsProducerView($tl.the_producer!.id),
        //     $tl.imported_events.getAll()
        // ]);

        const [all_events, autofill] = await Promise.all([
            cEventsProducerView(the_producer!.id),
            cProducerGetAutofill(the_producer!.id)
        ]);

        this._all_events = all_events.map(e => ({ sort: newCalcEventSort(e), event: e }));
        // this._all_events.push(...all_imported.filter(i => i.model.producer == producer_id && i.next_session).map(i => ({ sort: calcImportedSort(i), imported: i })));
        this._all_events.sort((a, b) => (a.sort[0] != b.sort[0] ? a.sort[0] - b.sort[0] : a.sort[1] - b.sort[1]));

        const filter_bar = this._filter_bar = new ABFilterBar({ on_search: doSearch, on_filter: doSearch });
        const filters = filter_bar.filters!;
        filters.addEventPills();

        // filters.addCity();
        // filters.addProducer();
        // filters.addGenre();

        // const show_sent = this._show_sent = new UICheckbox({ title: 'Show sent' });
        // const include_integrations = this._include_integrations = new UICheckbox({ title: 'Include integrations' });
        // this.search_bar.ui_insert([include_integrations, show_sent], this.object_count);

        //         super.fillContent(content);

        //         const segmented = this._segmented = new UISegmented([{ key: 'active', name: $i18n.aa.o_active }, { key: 'past', name: $i18n.aa.o_past }]);
        //         segmented.value = 'active';

        //         this.search_bar.ui_prepend(segmented);

        const grid = this._grid = new UIDataAdapter(e => e.event ? new EventCard(e.event, autofill) : new ImportedCard(e.imported, autofill));

        content.ui_add($el.DIV('ui-section', $el.DIV('tl-new-list-block', [
            $el.P('screen-title', [$i18n.pr.events_title, $el.DIV('s-flex'), $el.DIV('r-buttons', [])]),
            $el.P('screen-mobile-title', $i18n.pr.events_mobile_title),
            filter_bar,
            new UIList(grid, { classes: 'r-grid', progressive: true })
        ])));

        this._doSearch();
    }

    private _doSearch() {
        let events = this._all_events || [];

        const statuses = this._filter_bar.filters.value['event_status'];
        $log('app', 'statuses', statuses);

        if(statuses) {
            events = events.filter(e => {
                if(e.event) {
                    if(!e.event.is_published) {
                        return statuses.includes('unpublished');
                    } else if(cEventNextSession(e.event)) {
                        return statuses.includes('live');
                    } else {
                        return statuses.includes('past');
                    }
                } else {
                    return statuses.includes('imported');
                }
            });
        }

        // if(this._segmented.value == 'past') {
        //     events = events.filter(e => e.active_sessions.length && !e.hasSessionsBetween($time.now(), undefined));
        // } else {
        // events = events.filter(e => !e.active_sessions.length || e.hasSessionsBetween($time.now(), undefined));
        // }

        events = this._filter_bar.search_input!.filterArray(events, (e, word) => {
            const event = e.event; // || e.imported;
            if(!event) return false;

            if(yGetI18N(event.name).toLowerCase().includes(word)) return true;
            const venue = c_venues.maybe(event.venue_id);
            if(venue?.name.toLowerCase().includes(word)) return true;
            // if(event.date_display.toLowerCase().includes(word)) continue;
            // if($se.canSeeAllEvents() && event.customer_name.toLowerCase().includes(word)) continue;
            return false;
        });

        this._grid.data = events;
    }

    override onViewDidReappear() {
        cEventsProducerView(the_producer!.id).then(events => {
            this._all_events = events.map(e => ({ sort: newCalcEventSort(e), event: e }));
            // this._all_events.push(...all_imported.filter(i => i.model.producer == producer_id && i.next_session).map(i => ({ sort: calcImportedSort(i), imported: i })));
            this._all_events.sort((a, b) => (a.sort[0] != b.sort[0] ? a.sort[0] - b.sort[0] : a.sort[1] - b.sort[1]));
            this._doSearch();
        });
    }
}